// components/ui/ErrorHandlerProvider.tsx
import { FlashStyleTopToast } from '@/components/ui/FlashStyleTopToast';
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { Platform } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useMedia } from 'tamagui';
import { useTopToast } from './TopToastProvider';

interface ErrorHandlerContextType {
  isLoading: boolean;
  setLoading: (loading: boolean) => void;
  showError: (msg: string) => void;
  showSuccess: (msg: string) => void;
}

const ErrorHandlerContext = createContext<ErrorHandlerContextType | undefined>(undefined);

export const useErrorHandler = () => {
  const ctx = useContext(ErrorHandlerContext);
  if (!ctx) throw new Error('useErrorHandler must be used within ErrorHandlerProvider');
  return ctx;
};

export const ErrorHandlerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isLoading, setLoading] = useState(false);
  const { showTopToast, dismissTopToast } = useTopToast();
  const insets = useSafeAreaInsets();
  const media = useMedia();
  const headerRowHeight = media.gtMd ? 85 : media.gtSm ? 72 : 64;
  const toastTop =
    Platform.OS === 'web' ? 90 : insets.top + headerRowHeight + 8;

  const setLoadingHandler = useCallback((loading: boolean) => setLoading(loading), []);

  const showError = useCallback((msg: string) => {
    showTopToast({
      autoDismissMs: 3000,
      maxWidth: 400,
      top: toastTop,
      horizontalAlign: 'right',
      enterXOffset: 120,
      exitXOffset: 120,
      enterYOffset: 0,
      exitYOffset: 0,
      animation: 'quick',
      content: (
        <FlashStyleTopToast
          type="error"
          title="Error"
          message={msg}
          onClose={dismissTopToast}
        />
      ),
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toastTop]);

  const showSuccess = useCallback((msg: string) => {
    showTopToast({
      autoDismissMs: 3000,
      maxWidth: 400,
      top: toastTop,
      horizontalAlign: 'right',
      enterXOffset: 120,
      exitXOffset: 120,
      enterYOffset: 0,
      exitYOffset: 0,
      animation: 'quick',
      content: (
        <FlashStyleTopToast
          type="success"
          title="Success"
          message={msg}
          onClose={dismissTopToast}
        />
      ),
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toastTop]);

  const contextValue = useMemo(
    () => ({ isLoading, setLoading: setLoadingHandler, showError, showSuccess }),
    [isLoading, setLoadingHandler, showError, showSuccess],
  );

  return (
    <ErrorHandlerContext.Provider value={contextValue}>
      {children}
    </ErrorHandlerContext.Provider>
  );
};
